import { useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FileText, Loader2, UploadCloud, Download, FileImage, File, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import * as pdfjsLib from "pdfjs-dist";
import * as XLSX from "xlsx";
import Markdown from "react-markdown";
import { analyzeDocumentText, extractTextFromImage } from "../lib/gemini";
import { useAppStore } from "../store/useAppStore";
import { readFileAsDataUrl } from "../lib/file-utils";
import type { AppDocument } from "../types";

const pdfWorkerUrl = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const CONTRACTS_ENTITY_ID = "contracts";
const CONTRACTS_ENTITY_TYPE = "account" as const;

type ExtractedDocument = {
  name: string;
  text: string;
};

const isPdfFile = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

const isImageFile = (file: File) =>
  file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(file.name);

const isTextLikeFile = (file: File) =>
  file.type.startsWith("text/") || /\.(txt|md|csv|json|html?|rtf)$/i.test(file.name);

const isDocxFile = (file: File) =>
  file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
  /\.docx$/i.test(file.name);

const isDocFile = (file: File) =>
  file.type === "application/msword" || /\.doc$/i.test(file.name);

const isSpreadsheetFile = (file: File) =>
  /\.(xlsx?|xlsb|ods|csv|tsv)$/i.test(file.name) ||
  file.type === "application/vnd.ms-excel" ||
  file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
  file.type === "text/csv";

async function extractPdfText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  let text = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const strings = content.items.map((item: any) => item.str);
    text += strings.join(" ") + "\n";
  }

  return text.trim();
}

async function extractWordText(file: File): Promise<string> {
  if (isDocxFile(file)) {
    const mammoth = (await import("mammoth")).default;
    const buffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    return (result.value || "").trim();
  }

  const { default: WordExtractor } = await import("word-extractor");
  const wordExtractor = new WordExtractor();
  const buffer = await file.arrayBuffer();
  const doc = await wordExtractor.extract(new Uint8Array(buffer));
  return (doc.getBody() || "").trim();
}

async function extractSpreadsheetText(file: File): Promise<string> {
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetTexts = workbook.SheetNames.map((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
    const trimmed = csv.trim();
    if (!trimmed) return "";
    return `### Sheet: ${sheetName}\n${trimmed}`;
  }).filter(Boolean);

  return sheetTexts.join("\n\n").trim();
}

async function extractTextFromFile(file: File): Promise<string> {
  if (isPdfFile(file)) return extractPdfText(file);

  if (isImageFile(file)) {
    const dataUrl = await readFileAsDataUrl(file);
    return (await extractTextFromImage(dataUrl, file.name)).trim();
  }

  if (isDocxFile(file) || isDocFile(file)) {
    return extractWordText(file);
  }

  if (isSpreadsheetFile(file)) {
    return extractSpreadsheetText(file);
  }

  if (isTextLikeFile(file)) {
    return (await file.text()).trim();
  }

  throw new Error(
    `Unsupported file type: ${file.name}. Try PDF, DOC, DOCX, spreadsheet, image, or plain text documents.`
  );
}

function getFileIcon(type: string) {
  if (type.includes("image")) return <FileImage className="w-8 h-8 text-blue-500" />;
  if (type.includes("pdf")) return <FileText className="w-8 h-8 text-red-500" />;
  return <File className="w-8 h-8 text-slate-500" />;
}

function formatFileSize(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function getStatusBadge(status?: string) {
  if (status === "analyzed") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Analyzed</Badge>;
  if (status === "processing") return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Processing</Badge>;
  if (status === "error") return <Badge variant="destructive">Needs attention</Badge>;
  return <Badge variant="outline">Uploaded</Badge>;
}

async function fileFromDataUrl(dataUrl: string, fileName: string, fileType: string) {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return Object.assign(blob, {
    name: fileName,
    lastModified: Date.now(),
    type: fileType || blob.type || "application/octet-stream",
  }) as File;
}

export default function Contracts() {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [processingDocumentId, setProcessingDocumentId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { documents, addDocument, updateDocument } = useAppStore();

  const contractDocuments = documents
    .filter((doc) => doc.entityId === CONTRACTS_ENTITY_ID && doc.entityType === CONTRACTS_ENTITY_TYPE)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

  const latestAnalyzedDocument = useMemo(
    () => contractDocuments.find((doc) => doc.analysisSummary && doc.processingStatus === "analyzed") ?? null,
    [contractDocuments]
  );

  const documentStats = useMemo(() => {
    const analyzed = contractDocuments.filter((doc) => doc.processingStatus === "analyzed").length;
    const processing = contractDocuments.filter((doc) => doc.processingStatus === "processing").length;
    const needsAttention = contractDocuments.filter((doc) => doc.processingStatus === "error").length;
    return {
      total: contractDocuments.length,
      analyzed,
      processing,
      needsAttention,
    };
  }, [contractDocuments]);

  const activeAnalysis = analysis ?? latestAnalyzedDocument?.analysisSummary ?? null;

  const processDocumentFile = async (file: File, docId: string, context: string) => {
    await updateDocument(docId, {
      processingStatus: "processing",
      analysisError: undefined,
    });

    try {
      const text = await extractTextFromFile(file);
      if (!text) {
        await updateDocument(docId, {
          processingStatus: "error",
          analysisError: "No readable text found in file",
          extractedText: "",
        });
        return null;
      }

      const result = await analyzeDocumentText(text, context);
      setAnalysis(result);
      await updateDocument(docId, {
        processingStatus: "analyzed",
        extractedText: text,
        analysisSummary: result,
        analysisError: undefined,
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to analyze document";
      await updateDocument(docId, {
        processingStatus: "error",
        analysisError: message,
      });
      throw error;
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    setIsAnalyzing(true);
    setAnalysis(null);

    const uploadedDocs: Array<{ id: string; name: string }> = [];
    const skippedFiles: string[] = [];

    try {
      for (const file of files) {
        try {
          const fileUrl = await readFileAsDataUrl(file);
          const persistedDoc = await addDocument({
            entityId: CONTRACTS_ENTITY_ID,
            entityType: CONTRACTS_ENTITY_TYPE,
            fileName: file.name,
            fileType: file.type || "application/octet-stream",
            sizeBytes: file.size,
            fileUrl,
            processingStatus: "processing",
          });

          if (!persistedDoc) {
            throw new Error(`Could not persist ${file.name}`);
          }

          const result = await processDocumentFile(file, persistedDoc.id, file.name);
          if (!result) {
            skippedFiles.push(`${file.name} (no readable text found)`);
            continue;
          }

          uploadedDocs.push({ id: persistedDoc.id, name: file.name });
        } catch (err) {
          console.error(err);
          skippedFiles.push(file.name);
        }
      }

      if (!uploadedDocs.length) {
        toast.error("No readable text could be extracted from the uploaded files.");
        return;
      }

      if (skippedFiles.length) {
        console.warn("Skipped unsupported/unreadable files:", skippedFiles);
      }

      toast.success(
        uploadedDocs.length > 1
          ? `Extracted and analyzed ${uploadedDocs.length} documents.`
          : "Document analyzed successfully"
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to parse and analyze document.");
    } finally {
      setIsAnalyzing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleReanalyzeDocument = async (doc: AppDocument) => {
    setProcessingDocumentId(doc.id);
    try {
      const file = await fileFromDataUrl(doc.fileUrl, doc.fileName, doc.fileType);
      const result = await processDocumentFile(file, doc.id, doc.fileName);
      if (result) {
        toast.success(`Re-analyzed ${doc.fileName}`);
      } else {
        toast.error(`No readable text found in ${doc.fileName}`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to re-analyze ${doc.fileName}`);
    } finally {
      setProcessingDocumentId(null);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1fr_auto] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Document room</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Contract parsing</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Contract Parsing</h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-500">Upload contracts, addendums, images, DOC files, spreadsheets, or plain text files to automatically identify financial terms and hidden risks.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-600">
              <span className="rounded-full bg-slate-100 px-3 py-1.5">{documentStats.total} saved</span>
              <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">{documentStats.analyzed} analyzed</span>
              <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-700">{documentStats.processing} processing</span>
              <span className="rounded-full bg-rose-50 px-3 py-1.5 text-rose-700">{documentStats.needsAttention} need attention</span>
            </div>
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="h-11 rounded-2xl bg-slate-950 px-5 font-semibold text-white shadow-sm hover:bg-slate-800"
          >
            <UploadCloud className="mr-2 h-4 w-4" /> Upload document
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Saved documents</p>
              <p className="text-2xl font-bold text-slate-900">{documentStats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Analyzed</p>
              <p className="text-2xl font-bold text-slate-900">{documentStats.analyzed}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <Loader2 className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Processing</p>
              <p className="text-2xl font-bold text-slate-900">{documentStats.processing}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Need attention</p>
              <p className="text-2xl font-bold text-slate-900">{documentStats.needsAttention}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <Card className="h-fit border-slate-200 shadow-sm">
          <CardHeader className="space-y-2 border-b border-slate-100 bg-slate-50/70">
            <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
              <UploadCloud className="h-5 w-5 text-blue-600" /> Upload Documents
            </CardTitle>
            <CardDescription>
              Upload contracts, addendums, images, DOC files, spreadsheets, or plain text files to automatically identify financial terms and hidden risks.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            <div
              onClick={() => !isAnalyzing && fileInputRef.current?.click()}
              className={`rounded-2xl border-2 border-dashed border-slate-300 p-8 flex flex-col items-center justify-center transition-colors ${!isAnalyzing ? "cursor-pointer hover:bg-slate-50" : "opacity-50 cursor-not-allowed"}`}
            >
              {isAnalyzing ? <Loader2 className="mb-3 h-10 w-10 animate-spin text-blue-500" /> : <UploadCloud className="mb-3 h-10 w-10 text-slate-400" />}
              <span className="text-sm font-medium text-slate-600 text-center">{isAnalyzing ? "Reading Documents..." : "Tap to upload documents"}</span>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.xlsb,.ods,.csv,.tsv,.txt,.md,.json,.html,.htm"
              multiple
              onChange={handleUpload}
            />
          </CardContent>
        </Card>

        <Card className="min-h-[400px] border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/70">
            <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
              <FileText className="w-5 h-5 text-blue-600" /> Latest Contract Summary
            </CardTitle>
            <CardDescription>
              {latestAnalyzedDocument
                ? `Most recent analysis comes from ${latestAnalyzedDocument.fileName}. It stays persisted on the document record.`
                : "The latest analyzed summary is shown here and persisted on the uploaded document records."}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            {activeAnalysis ? (
              <div className="prose prose-sm max-w-none prose-slate prose-headings:scroll-m-20 prose-headings:tracking-tight">
                <Markdown>{activeAnalysis}</Markdown>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-400">
                <FileText className="mb-4 h-12 w-12 opacity-50" />
                <p>Your contract analysis will appear here. The AI will flag contingencies, check financial terms, and highlight risks.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/70">
          <CardTitle className="text-lg text-slate-900">Saved Contract Uploads</CardTitle>
          <CardDescription>
            Uploaded files are stored as CRM document records so they persist after refresh and can be reopened later.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {contractDocuments.length === 0 ? (
            <div className="m-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
              <UploadCloud className="mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">No contract documents saved yet</p>
              <p className="mt-1 max-w-[240px] text-xs text-slate-400">Upload a contract, and it will stay available after reload.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 flex flex-col">
              {contractDocuments.map((doc) => (
                <div key={doc.id} className="flex flex-col gap-3 p-4 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="shrink-0 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">{getFileIcon(doc.fileType)}</div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{doc.fileName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{formatFileSize(doc.sizeBytes)}</span>
                        <span>•</span>
                        <span>{new Date(doc.uploadedAt).toLocaleString()}</span>
                        <span>•</span>
                        <span className="uppercase tracking-wide font-semibold">{doc.processingStatus || "uploaded"}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {getStatusBadge(doc.processingStatus)}
                        {doc.analysisSummary ? <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Analysis ready</span> : null}
                      </div>
                      {doc.analysisSummary ? <p className="mt-2 line-clamp-2 text-xs text-slate-500">{doc.analysisSummary.slice(0, 180)}{doc.analysisSummary.length > 180 ? "…" : ""}</p> : null}
                      {doc.analysisError ? <p className="mt-2 text-xs text-red-600">{doc.analysisError}</p> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 rounded-2xl border-slate-200 bg-white"
                      onClick={() => handleReanalyzeDocument(doc)}
                      disabled={processingDocumentId === doc.id}
                    >
                      {processingDocumentId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      {processingDocumentId === doc.id ? "Analyzing..." : "Re-analyze"}
                    </Button>
                    <a href={doc.fileUrl} download={doc.fileName} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="gap-2 rounded-2xl border-slate-200 bg-white">
                        <Download className="h-4 w-4" /> Download
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
